import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { ErrorModal, SuccessModal } from "@components";
import {
  RequisitionDetailsPageHeader,
  RequisitionDetailsBody,
} from "./components";
import AssignModal from "./components/AssignModal";

import { useRequisition } from "@hooks/inventory/requisition/useRequisition";

import useCustomSelector from "@redux/hooks/useCustomSelector";

import { ROUTES } from "@constants/routes";
import {
  RequisitionOfficeInformationType,
  RequisitionProductDataType,
} from "@type/inventory/requisition-details";
import useActionCreator from "@redux/hooks/useActionCreator";

export type CustomSelectedProducts = {
  id: number;
  modalId: number | null;
  notAssignedAmount: number;
  products: any[];
};

const RequisitionDetails = () => {
  const navigate = useNavigate();
  const { assignRequisition } = useActionCreator();
  const { requisitionState } = useRequisition();
  const { getSingleData } = requisitionState;

  const [isOpen, setIsOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [data, setData] = useState<any>({});
  const [selectedProducts, setSelectedProducts] = useState<
    CustomSelectedProducts[] | []
  >([]);

  const [assignedProducts, setAssignedProducts] = useState<any[]>([]);
  const [assignErrorMessage, setAssignErrorMessage] = useState("");

  const [showAssignButton, setShowAssignButton] = useState(false);
  const [isApproved, setIsApproved] = useState(true);

  const { approveSuccess, assignSuccess, approveError, assignError } =
    useCustomSelector((store) => store.inventory.requisitionState);

  const handleProcessProduct = (rowData: RequisitionProductDataType) => {
    const __data = {
      data: rowData,
      employee: getSingleData?.employee,
    };
    setData(__data);
    setIsOpen(true);
  };

  const handleProductSelection = (item: any) => {
    const findModalIndex = selectedProducts.findIndex(
      (item: any) => item?.modalId === data?.data?.id,
    );
    if (findModalIndex === -1) {
      setSelectedProducts((prev: any) => {
        const __item = {
          modalId: data?.data?.id,
          id: data?.data?.id,
          notAssignedAmount:
            data?.data?.approvedAmount - data?.data?.approvedTotalAmount,
          products: [item],
        };
        return [...prev, __item];
      });
    } else {
      const selectedModal = selectedProducts[findModalIndex];
      if (!selectedModal?.products?.some((obj: any) => obj.id === item.id)) {
        selectedModal.products.push(item);
        setSelectedProducts((prev: any) => {
          return [...prev];
        });
      }
    }
  };

  const handleProductsRemove = (id: number | string) => {
    setSelectedProducts((prev: any) => {
      prev.forEach((item: CustomSelectedProducts) => {
        if (item?.products) {
          item.products = item.products.filter((obj: any) => obj.id !== id);
        }
      });
      return [...prev];
    });
  };

  const handleAssignProducts = () => {
    if (!selectedProducts.length) {
      setIsErrorModalOpen(true);
      setAssignErrorMessage("Click on Process button first then Assign");
    } else {
      const { employee, officeInformation } = getSingleData || {};
      const { id: employeeId } = employee || {};

      const {
        reqNo: requisitionId,
        reqFor: requestFor,
        deskId,
        roomId,
      } = (officeInformation as RequisitionOfficeInformationType) || {};

      const __selectedProducts = assignedProducts.map((item: any) => {
        return {
          employeeId: employeeId,
          requisitionId: requisitionId,
          requestFor: requestFor,
          deskId: deskId,
          roomId: roomId,

          itemId: item.type === "ITEM" ? item.itemId : null,
          nameId: item.nameId,
          productId: item.productId,
          assetId: item.type === "ASSET" ? item.assetId : null,
          amount:
            item.type === "ITEM"
              ? item.assignAmount
                ? item.assignAmount
                : null
              : null,
          type: item.type,
        };
      });

      assignRequisition({ data: __selectedProducts });
    }
  };

  const handleAssignAmount = (id: number, amount: string | number) => {
    // find the opened modal index
    const findModalIndex = selectedProducts.findIndex(
      (item: any) => item?.modalId === data?.data?.id,
    );

    // the selected modal
    const selectedModal = selectedProducts[findModalIndex];

    // find the product index in the selected modal
    const findProductIndex = selectedModal.products.findIndex(
      (item: any) => item.id === id,
    );

    // assign the amount to the selected product
    selectedModal.products[findProductIndex].assignAmount = Number(amount);

    setSelectedProducts((prev: any) => {
      return [...prev];
    });
  };

  const showAssignButtonHandler = (data: boolean): void => {
    setShowAssignButton(data);
  };

  const clearSelection = () => {
    setSelectedProducts((prev: CustomSelectedProducts[] | []) => {
      // remove the selected products of the closed modal
      return prev.filter(
        (item: CustomSelectedProducts) => item.modalId !== data?.data?.id,
      );
    });
  };

  const approveSuccessAction = () => {
    navigate(`${ROUTES.INVENTORY.REQUISITION}`);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const approveOrRejectHandler = (type: boolean) => {
    setIsApproved(type);
  };

  useEffect(() => {
    if (approveSuccess || assignSuccess) {
      setIsSuccessModalOpen(true);
    } else {
      setIsSuccessModalOpen(false);
    }
    if (approveError || assignError) {
      setIsErrorModalOpen(true);
      if (assignError.response && assignError.response.data) {
        setAssignErrorMessage(
          assignError.response.data.message || "something was wrong",
        );
      }
    } else {
      setIsErrorModalOpen(false);
    }
  }, [approveSuccess, assignSuccess, assignError, approveError]);

  useEffect(() => {
    if (selectedProducts.length) {
      const result: any = [];
      selectedProducts.forEach((product) => {
        if (product?.products) {
          result.push(product.products);
        }
      });

      setAssignedProducts(result.flat());
    }
  }, [selectedProducts]);

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
      setAssignErrorMessage("");
    };
    return reset();
  }, []);

  return (
    <div className="p-10">
      <RequisitionDetailsPageHeader
        handleAssignProducts={handleAssignProducts}
        showAssignButton={showAssignButton}
        data={getSingleData}
      />
      <RequisitionDetailsBody
        handleProductsRemove={handleProductsRemove}
        selectedProducts={assignedProducts}
        data={getSingleData}
        approveOrRejectHandler={approveOrRejectHandler}
        showAssignButtonHandler={showAssignButtonHandler}
        handleProcessProduct={handleProcessProduct}
      />
      {isOpen && (
        <AssignModal
          isOpen={isOpen}
          onClose={onClose}
          rowData={data}
          handleProductSelection={handleProductSelection}
          selectedProducts={selectedProducts}
          clearSelection={clearSelection}
          handleAssignAmount={handleAssignAmount}
        />
      )}

      {/* modals  */}
      {isSuccessModalOpen && (
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={approveSuccessAction}
          subtitle={
            approveSuccess
              ? isApproved
                ? "Approved"
                : "Rejected"
              : assignSuccess
              ? "Assigned"
              : ""
          }
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
          subtitle={assignErrorMessage}

          //
        />
      )}
    </div>
  );
};

export default RequisitionDetails;
